import {
  Component,
  ComponentRef,
  computed,
  effect,
  inject,
  inputBinding,
  resource,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { Router } from '@angular/router';
import { ApiClient } from '@core/services/api-client';
import { Badge } from '@shared/components/badge/badge';
import { Button } from '@shared/components/button/button';
import { Modal } from '@shared/components/modal/modal';
import { Toast, ToastMessage } from '@shared/components/toast/toast';
import { AllProblems, Problem } from '@shared/models/problem';

@Component({
  selector: 'app-problems-page',
  imports: [Button, Badge, Modal],
  templateUrl: './problems-page.html',
})
export class ProblemsPage {
  private apiClient = inject(ApiClient);
  private router = inject(Router);

  // Toast functionality
  private toastRef: ComponentRef<Toast> | null = null;
  readonly toastContainer = viewChild.required('toastOutlet', {
    read: ViewContainerRef,
  });
  private toastMessage = signal<string>('');

  // Delete modal
  readonly deleteModal = viewChild.required('deleteModal', { read: Modal });
  private problemToDelete = signal<string>('');

  //problem Data
  readonly problems = resource({
    loader: () => this.apiClient.getAllProblems(),
  });

  readonly isProblemsLoading = computed(
    () => this.problems.status() === 'loading'
  );

  readonly problemsData = computed(
    () => this.problems.value() as AllProblems | undefined
  );

  readonly allProblems = signal<Problem[]>([]);

  constructor() {
    effect(() => {
      const data = this.problemsData();
      if (data) {
        this.allProblems.set([...data.problems]);
      }
    });
  }

  // Toast functionality
  private showToast(type: ToastMessage['type'], message: string): void {
    if (!this.toastRef) {
      this.toastRef = this.toastContainer().createComponent(Toast, {
        bindings: [inputBinding('toastMessage', this.toastMessage)],
      });
    }
    this.toastMessage.set(message);
    this.toastRef.instance.show(type, message);
  }

  stripHtmlTags(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  async deleteProblem(problemId: string) {
    this.problemToDelete.set(problemId);
    this.deleteModal().open();
  }

  async confirmDelete() {
    try {
      const problemId = this.problemToDelete();
      if (!problemId) return;

      await this.apiClient.deleteProblem(problemId);
      this.problems.reload();
      this.showToast('success', 'Problem deleted successfully');
      this.problemToDelete.set('');
    } catch (error) {
      console.error('Failed to delete problem:', error);
      this.showToast('error', 'Failed to delete problem. Please try again.');
    }
  }

  goToCreateProblem() {
    this.router.navigate(['/problems/create']);
  }

  goToViewProblem(problemId: string) {
    this.router.navigate(['/problems/view', problemId]);
  }

  goToEditProblem(problemId: string) {
    this.router.navigate(['/problems/edit', problemId]);
  }
}
